import { Flex, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"

const { Text } = Typography

interface MilestoneFinancialCellProps {
  settled: number | null
  planned: number | null
  /** The project's current Contract Value (Receivables) or Contract
   * Expenses (Payables) — the same figure the header and Financial Alerts
   * panel compare against, so a row never disagrees with them. */
  contractLimit?: number | null
  contractLabel?: string
}

/**
 * Two figures, independently hoverable and independently flagged. Planned
 * is what the milestone intends, Settled is what has actually been paid —
 * each number carries its own tooltip, and each is marked red on its own
 * merits: a value exceeding the current contract limit, nothing else.
 *
 * The comparison reads the same `sales_price` / `planned_payable` figures
 * the header and Financial Alerts panel already use — never a value
 * recomputed just for this cell.
 */
export const MilestoneFinancialCell = ({
  settled,
  planned,
  contractLimit,
  contractLabel = "Contract"
}: MilestoneFinancialCellProps) => {
  const { t } = useTranslation()
  const safeSettled = Number(settled) || 0
  const safePlanned = Number(planned) || 0
  const limit = Number(contractLimit) || 0

  const settledExceeds = limit > 0 && safeSettled > limit
  const plannedExceeds = limit > 0 && safePlanned > limit

  const classFor = (exceeds: boolean) =>
    exceeds ? "text-red-500 font-bold" : "text-teal-600 font-medium"

  const tooltipFor = (label: string, value: number, exceeds: boolean) => (
    <Flex vertical gap={2} className="text-white text-xs">
      <Text className="text-white text-xs">
        {label}: {budgetFormat(value)}
      </Text>
      {exceeds && (
        <>
          <Text className="text-white text-xs">
            {contractLabel}: {budgetFormat(limit)}
          </Text>
          <Text className="text-red-400 text-xs font-semibold">
            Exceeds contract by {budgetFormat(value - limit)}
          </Text>
        </>
      )}
    </Flex>
  )

  return (
    <Flex justify="center" gap={4} className="text-center">
      <Tooltip
        title={tooltipFor(
          t("milestoneMoney.settled", "Settled"),
          safeSettled,
          settledExceeds
        )}
      >
        <Text data-testid="milestone-settled" className={classFor(settledExceeds)}>
          {budgetFormat(safeSettled)}
        </Text>
      </Tooltip>
      <Text className="text-teal-600 font-medium">/</Text>
      <Tooltip
        title={tooltipFor(
          t("milestoneMoney.planned", "Planned"),
          safePlanned,
          plannedExceeds
        )}
      >
        <Text data-testid="milestone-planned" className={classFor(plannedExceeds)}>
          {budgetFormat(safePlanned)}
        </Text>
      </Tooltip>
    </Flex>
  )
}
